import React from 'react';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {TextField, type TextFieldProps} from '@netx/core-components/TextField';

type TextFieldRequiredProps = Required<TextFieldProps>;

const getNumberWithoutDuplicateDecimals = (str: string) => {
	let counter = 0;
	str = str.replace(/(\d)-|(-)-/g, '$1$2');
	str = str.replace(/[^\d.-]/g, '');
	return str.replace(/[^0-9.-]|\./g, function ($0) {
		if ($0 == '.' && !counter++)
			// dot found and counter is not incremented
			return '.'; // that means we met first dot and we want to keep it
		return ''; // if we find anything else, let's erase it
	});
};

const defaultFormatter = (value: string) => value;

export interface NumberFieldProps extends Omit<TextFieldProps, 'value'> {
	maxLength?: number;
	value?: string | number;
	format?: (value: string) => string;
}

export const NumberFieldPrimitive = (
	{
		maxLength,
		value,
		format = defaultFormatter,
		onChange,
		...props
	}: NumberFieldProps,
	ref: React.ForwardedRef<HTMLDivElement>,
) => {
	const handleChange = useConstCallback<TextFieldRequiredProps['onChange']>(
		(event, value) =>
			onChange?.(event, format(getNumberWithoutDuplicateDecimals(value))),
	);

	return (
		<TextField
			ref={ref}
			{...props}
			inputProps={{
				...props?.inputProps,
				maxLength,
			}}
			onChange={handleChange}
			value={value != null ? value.toString() : value}
		/>
	);
};

/**
 * `TextField` with number formatting and validation.
 */
export const NumberField = React.forwardRef<HTMLDivElement, NumberFieldProps>(
	NumberFieldPrimitive,
);

export default NumberField;
